// Installed app version. On native (Capacitor Android) this is the APK
// versionName + versionCode reported by App.getInfo(). On the web there is
// no installed version — callers must treat that case as "unknown" and skip
// any force-update comparison.
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { isNativePluginAvailable } from "./upi-native";

let cachedVersion: string | null = null;
let cachedBuild: number | null = null;
let resolved = false;
let inflight: Promise<{ version: string | null; build: number | null }> | null = null;

async function tryGetInfo(): Promise<{ version: string | null; build: number | null }> {
  const pluginAvailable =
    (Capacitor as any)?.isPluginAvailable?.("App") === true ||
    isNativePluginAvailable("App");
  if (!pluginAvailable) {
    console.warn("[version] App native plugin is not available on this build.");
    return { version: null, build: null };
  }
  const info = await App.getInfo();
  console.info("[version] App.getInfo() result:", info);
  const v = (info?.version || "").trim();
  const b = Number.parseInt(String(info?.build ?? ""), 10);
  return {
    version: v || null,
    build: Number.isFinite(b) && b > 0 ? b : null,
  };
}

async function resolveNow() {
  if (resolved) return { version: cachedVersion, build: cachedBuild };
  if (inflight) return inflight;
  inflight = (async () => {
    if (!Capacitor.isNativePlatform()) {
      console.info("[version] non-native platform; skipping App.getInfo()");
      cachedVersion = null;
      cachedBuild = null;
    } else {
      let got: { version: string | null; build: number | null } | null = null;
      try {
        got = await tryGetInfo();
      } catch (err) {
        console.warn("[version] App.getInfo() failed, retrying in 400ms:", err);
        await new Promise((r) => setTimeout(r, 400));
        try {
          got = await tryGetInfo();
        } catch (err2) {
          console.error("[version] App.getInfo() failed after retry:", err2);
          got = { version: null, build: null };
        }
      }
      cachedVersion = got.version;
      cachedBuild = got.build;
    }
    resolved = true;
    return { version: cachedVersion, build: cachedBuild };
  })();
  return inflight;
}

export async function getInstalledVersion(): Promise<string | null> {
  return (await resolveNow()).version;
}

export async function getInstalledBuild(): Promise<number | null> {
  return (await resolveNow()).build;
}

export async function getInstalledInfo(): Promise<{ version: string | null; build: number | null }> {
  return resolveNow();
}

export function getInstalledVersionSync(): string | null {
  return cachedVersion;
}

export function getInstalledBuildSync(): number | null {
  return cachedBuild;
}

/** @deprecated Kept only so old imports keep type-checking. */
export const APP_VERSION = "";

/** Compare dotted numeric versions. Returns -1, 0, 1. */
export function compareVersions(a: string, b: string): number {
  const pa = String(a || "0").split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b || "0").split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

if (typeof window !== "undefined") {
  void resolveNow();
}
